"use strict";
var _a;
Object.defineProperty(exports, "__esModule", { value: true });
exports.AuthorizationService = void 0;
const tslib_1 = require("tslib");
const common_1 = require("@nestjs/common");
const typeorm_1 = require("@nestjs/typeorm");
const typeorm_2 = require("typeorm");
const permissions_1 = require("./permissions");
const role_entity_1 = require("./role.entity");
const system_roles_1 = require("./system-roles");
let AuthorizationService = class AuthorizationService {
    constructor(roleRepo) {
        this.roleRepo = roleRepo;
        this.validPermissionKeys = new Set(permissions_1.PERMISSION_CATALOG.map(permission => permission.key));
    }
    async onModuleInit() {
        await this.ensureSystemRoles();
    }
    async ensureSystemRoles() {
        const existing = await this.roleRepo.find({
            where: { key: (0, typeorm_2.In)(system_roles_1.SYSTEM_ROLE_DEFINITIONS.map(role => role.key)) },
        });
        const existingMap = new Map(existing.map(role => [role.key, role]));
        // Always upsert — create missing, update permissions on existing so code
        // changes to ROLE_PERMISSIONS are reflected without manual DB edits.
        const toSave = system_roles_1.SYSTEM_ROLE_DEFINITIONS.map(def => {
            const existing = existingMap.get(def.key);
            if (existing) {
                existing.permissions = def.permissions;
                existing.name = def.name;
                existing.description = def.description;
                return existing;
            }
            return this.roleRepo.create(def);
        });
        await this.roleRepo.save(toSave);
    }
    async getPermissionsForRole(roleKey) {
        if (!roleKey)
            return [];
        const role = await this.roleRepo.findOne({
            where: { key: roleKey },
        });
        return [...new Set((role?.permissions ?? []))];
    }
    listPermissionCatalog() {
        return permissions_1.PERMISSION_CATALOG;
    }
    async listRoles() {
        return this.roleRepo.find({
            order: { isSystem: 'DESC', name: 'ASC' },
        });
    }
    async listRoleOptions() {
        const roles = await this.listRoles();
        return roles.map(role => ({
            key: role.key,
            label: role.name,
            isSystem: role.isSystem,
        }));
    }
    async createRole(input) {
        const key = input.key.trim().toLowerCase();
        this.validateRoleKey(key);
        this.validatePermissions(input.permissions);
        const existing = await this.roleRepo.findOne({ where: { key } });
        if (existing) {
            throw new common_1.BadRequestException('A role with this key already exists');
        }
        const role = this.roleRepo.create({
            key,
            name: input.name.trim(),
            description: input.description?.trim() || null,
            permissions: [...new Set(input.permissions)],
            isSystem: false,
        });
        return this.roleRepo.save(role);
    }
    async updateRole(key, input) {
        const role = await this.roleRepo.findOne({ where: { key } });
        if (!role)
            throw new common_1.NotFoundException('Role not found');
        this.validatePermissions(input.permissions);
        role.name = input.name.trim();
        role.description = input.description?.trim() || null;
        role.permissions = [...new Set(input.permissions)];
        return this.roleRepo.save(role);
    }
    hasAllPermissions(grantedPermissions, requiredPermissions) {
        if (!requiredPermissions.length)
            return true;
        if (!grantedPermissions?.length)
            return false;
        const granted = new Set(grantedPermissions);
        return requiredPermissions.every(permission => granted.has(permission));
    }
    validatePermissions(permissions) {
        const invalid = permissions.filter(permission => !this.validPermissionKeys.has(permission));
        if (invalid.length) {
            throw new common_1.BadRequestException(`Unknown permissions: ${invalid.join(', ')}`);
        }
    }
    validateRoleKey(key) {
        if (!/^[a-z0-9_]+$/.test(key)) {
            throw new common_1.BadRequestException('Role key must use lowercase letters, numbers, and underscores only');
        }
    }
};
exports.AuthorizationService = AuthorizationService;
exports.AuthorizationService = AuthorizationService = tslib_1.__decorate([
    (0, common_1.Injectable)(),
    tslib_1.__param(0, (0, typeorm_1.InjectRepository)(role_entity_1.Role)),
    tslib_1.__metadata("design:paramtypes", [typeof (_a = typeof typeorm_2.Repository !== "undefined" && typeorm_2.Repository) === "function" ? _a : Object])
], AuthorizationService);
